/*
 * Theme-native sticky add-to-cart bar.
 *
 * Replaces the Essential Cart Drawer app's sticky bar. Critically, its
 * "Add to cart" submits the SAME product form as the on-page button
 * (product-form-{section}), so both buttons post identical payloads and
 * Shopify always merges them into one cart line (fixes the duplicate-line bug).
 *
 * Stays in sync with the main variant picker via the theme's variantChange
 * pub/sub event, and drives the main picker when its own dropdown changes.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;

        this.form = document.getElementById(`product-form-${this.section}`);
        this.mainQty = document.getElementById(`Quantity-${this.section}`);
        this.variantSelects = document.getElementById(`variant-selects-${this.section}`);
        this.anchor = document.getElementById(`ProductSubmitButton-${this.section}`);

        this.select = this.querySelector('[data-sticky-variant]');
        this.qtyInput = this.querySelector('[data-sticky-qty-input]');
        this.addBtn = this.querySelector('[data-sticky-add]');
        this.addBtnText = this.addBtn ? this.addBtn.querySelector('span') : null;
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.imageEl = this.querySelector('[data-sticky-image]');

        // Variants lookup (id -> variant)
        this.variants = {};
        try {
          const data = JSON.parse(this.querySelector('[data-sticky-variants]').textContent);
          data.forEach((v) => (this.variants[v.id] = v));
        } catch (e) {
          this.variants = {};
        }

        // Quantity steppers
        this.querySelector('[data-sticky-qty-minus]')?.addEventListener('click', () => this.stepQty(-1));
        this.querySelector('[data-sticky-qty-plus]')?.addEventListener('click', () => this.stepQty(1));
        // Clamp typed values in the sticky counter to available stock
        this.qtyInput?.addEventListener('change', () => this.clampQty());
        // Also clamp typed values in the on-page counter (its max is inventory-aware)
        this.mainQty?.addEventListener('change', () => {
          const max = this.mainQty.getAttribute('max');
          const v = parseInt(this.mainQty.value, 10);
          if (max && !isNaN(v) && v > parseInt(max, 10)) this.mainQty.value = max;
        });

        // Variant dropdown drives the main picker
        this.select?.addEventListener('change', () => this.onSelectVariant());

        // Add to cart -> submit the real product form (same path as on-page button)
        this.addBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          this.add();
        });

        // Keep in sync with the main picker
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
        }

        // Show/hide on scroll, keyed to the on-page add-to-cart button
        if (this.anchor && 'IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entries) => this.toggle(!entries[0].isIntersecting),
            { threshold: 0 }
          );
          this.io.observe(this.anchor);
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsub) this.variantChangeUnsub();
        if (this.io) this.io.disconnect();
      }

      toggle(show) {
        this.classList.toggle('sticky-atc--visible', show);
      }

      // Effective stock/order cap — read live from the on-page input, whose max
      // is kept inventory-aware (min of quantity rule and available stock).
      getCap() {
        if (this.mainQty) {
          const m = this.mainQty.getAttribute('max');
          if (m !== null && m !== '') return parseInt(m, 10);
        }
        const own = this.qtyInput?.getAttribute('max');
        return own ? parseInt(own, 10) : null;
      }

      getMin() {
        const ref = this.mainQty || this.qtyInput;
        return parseInt(ref.getAttribute('min') || '1', 10) || 1;
      }

      clampQty() {
        if (!this.qtyInput) return;
        const min = this.getMin();
        const cap = this.getCap();
        let v = parseInt(this.qtyInput.value, 10);
        if (isNaN(v) || v < min) v = min;
        if (cap !== null && !isNaN(cap)) v = Math.min(cap, v);
        if (v < min) v = min;
        this.qtyInput.value = v;
      }

      stepQty(dir) {
        const step = parseInt((this.mainQty || this.qtyInput).getAttribute('step') || '1', 10) || 1;
        this.qtyInput.value = (parseInt(this.qtyInput.value, 10) || this.getMin()) + dir * step;
        this.clampQty();
      }

      // Sticky dropdown changed -> reflect it in the main variant picker,
      // which re-renders the whole product info (price, media, form id...).
      onSelectVariant() {
        const variant = this.variants[this.select.value];
        if (!variant || !this.variantSelects) return;

        (variant.options || []).forEach((val, idx) => {
          const pos = idx + 1;
          const radios = this.variantSelects.querySelectorAll(`input[name$="-${pos}"]`);
          if (radios.length) {
            let selected = null;
            radios.forEach((r) => {
              r.checked = r.value === val;
              if (r.checked) selected = r;
            });
            if (selected) selected.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            // dropdown <select> fallback (no position in name)
            const sel = this.variantSelects.querySelectorAll('select')[idx];
            if (sel) {
              sel.value = val;
              sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      }

      // Main picker changed -> mirror into the sticky bar.
      onVariantChange(event) {
        const data = event?.data;
        if (!data || String(data.sectionId) !== String(this.section)) return;

        const variant = data.variant;

        // Sync the dropdown selection
        if (this.select && variant) this.select.value = variant.id;

        // Mirror the freshly-rendered price markup (exact theme formatting)
        if (this.priceEl && data.html) {
          const priceNode = data.html.querySelector(`[id="price-${this.section}"]`);
          if (priceNode) this.priceEl.innerHTML = priceNode.innerHTML;
        }

        // Swap thumbnail to the variant image when available
        if (this.imageEl && variant && variant.featured_image && variant.featured_image.src) {
          this.imageEl.src = variant.featured_image.src;
        }

        // Reflect availability on the button
        this.setAvailability(variant);

        // New variant => new stock cap; re-clamp the current quantity
        this.clampQty();
      }

      setAvailability(variant) {
        if (!this.addBtn) return;
        const unavailable = !variant || variant.available === false;
        this.addBtn.toggleAttribute('aria-disabled', unavailable);
        if (this.addBtnText && window.variantStrings) {
          this.addBtnText.textContent = !variant
            ? window.variantStrings.unavailable
            : variant.available === false
            ? window.variantStrings.soldOut
            : window.variantStrings.addToCart;
        }
      }

      add() {
        if (!this.form || this.addBtn.getAttribute('aria-disabled') === 'true') return;

        // Never submit more than available stock
        this.clampQty();

        // Push the sticky quantity into the real form before submitting
        if (this.mainQty && this.qtyInput) this.mainQty.value = this.qtyInput.value;

        // Submit through the on-page <product-form> handler (identical payload)
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      }
    }
  );
}
